import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOConfig {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'product' | 'article';
  noindex?: boolean;
  structuredData?: object;
  language?: 'en' | 'fa';
}

const defaults = {
  title: "RUBBY — Toys, Storybooks & Animation from Kids' Art",
  description: "Turn your child's drawing or photo into a custom 3D-printed toy, a personalised storybook, or an animated short film. UK-based, child-safe, delivered fast.",
  image: '/og-image.png',
  url: 'https://rubby.co.uk',
};

export function SEOHead({
  title,
  description,
  image,
  url,
  type = 'website',
  noindex = false,
  structuredData,
  language,
}: SEOConfig) {
  const { language: ctxLanguage } = useLanguage();
  const lang = language ?? ctxLanguage;
  const pageTitle = title ? `${title} | RUBBY` : defaults.title;
  const pageDescription = description || defaults.description;
  const pageImage = image || defaults.image;
  const pageUrl = url || defaults.url;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="RUBBY" />
      <meta property="og:locale" content={lang === 'fa' ? 'fa_IR' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

